import type { TConstraints } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import type { FacebookCampaign } from './campaigns';
import type { ICreativeAttributes, IDimensionDefinition } from './creatives_base';
import type { IDisplayCreative } from './creatives_display';
import { DisplayCreative } from './creatives_display';

// =============================================================================
// Meta Dimension Validator
// =============================================================================
// Unlike display ads which require exact dimensions, Meta accepts images larger
// than the spec dimensions as long as the aspect ratio matches. Meta's platform
// handles scaling during ad delivery, so we validate aspect ratio + minimum size.
// =============================================================================

/**
 * Aspect ratio tolerance for Meta dimension validation.
 * Allows for slight variations due to rounding (e.g., 1.91:1 vs 1.9108:1).
 * Exported so frontend validation can use the same tolerance.
 */
export const META_ASPECT_RATIO_TOLERANCE = 0.01;

/**
 * Checks if two aspect ratios match within the allowed tolerance.
 * Exported so frontend validation can use the same logic.
 *
 * @param ratio1 - First aspect ratio (width/height)
 * @param ratio2 - Second aspect ratio (width/height)
 * @returns True if the ratios match within tolerance
 */
export function metaAspectRatiosMatch(ratio1: number, ratio2: number): boolean {
  return Math.abs(ratio1 - ratio2) <= META_ASPECT_RATIO_TOLERANCE;
}

/**
 * Creates a dimension validator for Meta/Facebook creatives.
 *
 * Unlike display ads, Meta accepts images larger than the spec dimensions as long as
 * the aspect ratio matches. Meta's platform handles scaling during ad delivery.
 *
 * Validates that:
 * 1. The aspect ratio matches the spec's aspect ratio (within ±0.01 tolerance)
 * 2. The dimensions are at least as large as the spec's minimum dimensions
 *
 * @param dimensions - The dimension definitions to validate against (e.g., ValidFacebookDimensions)
 * @param attribute - Which attribute to validate ('width' or 'height')
 * @returns A validator function for use in model constraints
 *
 * @example
 * // A 1080x1080 square spec accepts:
 * // - 1080x1080 (exact match)
 * // - 2000x2000 (same 1:1 ratio, larger than minimum)
 * // - 4000x4000 (same 1:1 ratio, larger than minimum)
 * //
 * // But rejects:
 * // - 500x500 (too small, below 1080 minimum)
 * // - 800x600 (wrong aspect ratio, not 1:1)
 *
 * @example
 * // In FacebookImageCreative constraints:
 * width: {
 *   equality: createMetaDimensionValidator(ValidFacebookDimensions, 'width'),
 * }
 */
export function createMetaDimensionValidator(
  dimensions: Record<string, IDimensionDefinition>,
  attribute: 'width' | 'height',
) {
  return (...args: any[]) => {
    const attributes = args[1];
    const { spec, width, height } = attributes;

    if (!spec) {
      return undefined;
    }

    const dimensionDef = Object.values(dimensions).find((d) => d.spec === spec);
    if (!dimensionDef) {
      return undefined;
    }

    const specAspectRatio = dimensionDef.width / dimensionDef.height;
    const actualAspectRatio = width / height;

    const aspectRatioValid = metaAspectRatiosMatch(actualAspectRatio, specAspectRatio);
    const dimensionMeetsMinimum = attributes[attribute] >= dimensionDef[attribute];

    return {
      attribute,
      message: `^${width}x${height} is not a valid creative size for this type`,
      comparator: (): boolean => aspectRatioValid && dimensionMeetsMinimum,
    };
  };
}

// =============================================================================
// Facebook Video Dimensions
// =============================================================================

export const ValidFacebookVideoDimensions: Record<string, IDimensionDefinition> = {
  '1080x1080': {
    name: 'Facebook Standard Square',
    spec: 'facebook_standard_square',
    width: 1080,
    height: 1080,
  },
  '1920x1080': {
    name: 'Facebook Landscape Video',
    spec: '1080p_facebook_landscape_video',
    width: 1920,
    height: 1080,
  },
  '1080x1350': {
    name: 'Facebook Vertical Video',
    spec: '1080p_facebook_vertical_video',
    width: 1080,
    height: 1350,
  },
  '1080x1920': {
    name: 'Facebook Full Portrait Video',
    spec: '1080p_facebook_full_portrait_video',
    width: 1080,
    height: 1920,
  },
  '1200x628': {
    name: 'Facebook Standard Video',
    spec: 'facebook_standard_video',
    width: 1200,
    height: 628,
  },
};

// =============================================================================
// Call to Action Types
// =============================================================================

export enum ECallToActionType {
  LEARN_MORE = 'LEARN_MORE',
  SIGN_UP = 'SIGN_UP',
  BUY_NOW = 'BUY_NOW',
  GET_OFFER = 'GET_OFFER',
}

export interface ICallToActionType {
  id: ECallToActionType;
  name: string;
}

export const CallToActionTypes: ICallToActionType[] = [
  { id: ECallToActionType.SIGN_UP, name: 'Sign Up' },
  { id: ECallToActionType.BUY_NOW, name: 'Buy Now' },
  { id: ECallToActionType.GET_OFFER, name: 'Get Offer' },
  { id: ECallToActionType.LEARN_MORE, name: 'Learn More' },
];

// =============================================================================
// Facebook Audit Status Types
// =============================================================================

export type TFacebookCreativeAuditStatus =
  | 'WITH_ISSUES'
  | 'DISAPPROVED'
  | 'PENDING_REVIEW'
  | 'IN_PROCESS'
  | 'ACTIVE'
  | 'PAUSED'
  | 'ARCHIVED';

// =============================================================================
// Facebook Ad Preview Types
// =============================================================================

export interface IFacebookAdPreviewRequest {
  ad_format: string;
  _cls: string;
  facebook_page_id: string;
  message: string;
  name: string;
  caption: string;
  description: string;
  url: string;
  destination_url: string;
  call_to_action_type: string;
}

export interface IFacebookAdReviewFeedback {
  feedback?: Record<string, string>;
  facebook?: Record<string, string>;
  instagram?: Record<string, string>;
}

// =============================================================================
// Facebook Display Creative Interface
// =============================================================================

export interface IFacebookDisplayCreative extends IDisplayCreative {
  description: string;
  message: string;
  caption: string;
  call_to_action_type: ECallToActionType;
  ad_status: TFacebookCreativeAuditStatus;
  ad_review_feedback: IFacebookAdReviewFeedback;
}

// =============================================================================
// Facebook Display Creative Class
// =============================================================================

/**
 * FacebookDisplayCreative is the abstract base class for Meta/Facebook creatives.
 * It provides Facebook-specific validation and functionality.
 */
export abstract class FacebookDisplayCreative<
  T extends IFacebookDisplayCreative = IFacebookDisplayCreative,
> extends DisplayCreative<T> {
  public override readonly className:
    | 'BannersnackCreative'
    | 'FacebookDisplayCreative'
    | 'FacebookImageCreative'
    | 'FacebookVideoCreative' = 'FacebookDisplayCreative';

  public override get constraints(): TConstraints<T> {
    return {
      ...super.constraints,
      message: {
        length: {
          maximum: 1000,
        },
      },
      description: {
        length: {
          maximum: 27,
        },
      },
      caption: {
        url: (...args: any[]) => {
          const attributes = args[1];
          const isEmpty = !attributes.caption;
          if (isEmpty) {
            return undefined;
          }
          return true;
        },
        length: {
          maximum: 25,
        },
      },
      call_to_action_type: {
        inclusion: {
          within: [
            ECallToActionType.BUY_NOW,
            ECallToActionType.GET_OFFER,
            ECallToActionType.LEARN_MORE,
            ECallToActionType.SIGN_UP,
          ],
        },
      },
    };
  }

  public override getDefaults(): Partial<T> {
    return {
      call_to_action_type: 'LEARN_MORE',
    } as Partial<T>;
  }

  /**
   * Gets a preview of the Facebook ad.
   * @param adFormat - The format of the ad to preview
   * @param campaign - The campaign associated with this creative
   * @returns The HTML body of the ad preview
   */
  public abstract getFacebookAdPreview(
    adFormat: string,
    campaign: FacebookCampaign,
  ): Promise<string>;

  /**
   * Submits a review request for this creative to Facebook.
   * @returns The response data from the review request
   */
  public async submitReviewRequest(): Promise<Record<string, unknown>> {
    this.assertCollection(this.collection);
    const url = `${this.collection.getHostname()}integrations/facebook/ad_request_review/${
      this.id
    }`;
    const response = await wretch<ICreativeAttributes>(url, {
      method: 'PATCH',
      headers: this.collection.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }
}
